const isAllowed = (item, ctx) => {
  if (item.roles && !item.roles.includes(ctx.role)) return false;
  if (item.visible && !item.visible(ctx)) return false;
  return true;
};

/** Returns the menu with everything the current user may not see removed. */
export const filterMenu = (menu, ctx) =>
  menu
    .map((section) => ({
      ...section,
      items: section.items
        .filter((item) => isAllowed(item, ctx))
        .map((item) => (item.children ? { ...item, children: item.children.filter((child) => isAllowed(child, ctx)) } : item))
        .filter((item) => !item.children || item.children.length > 0),
    }))
    .filter((section) => section.items.length > 0);

// Routes in this app follow several naming styles for detail pages:
// /create-staff, /createdesignation, /edit-grade/1, /edit/grn/1, /factory/create,
// /issue-view/1, /chart-of-account-ledger/1 ... all of which belong to one menu entry.
const ACTION_PREFIX = /^(create|edit|show|view)-?/;
const DETAIL_SUFFIX = /-(view|details|ledger)$/;

const firstSegment = (path) => {
  const segments = path.toLowerCase().split("/").filter(Boolean);
  if (segments[0] === "edit" || segments[0] === "create") return segments[1] || "";
  return segments[0] || "";
};

const normalizeSegment = (segment) => segment.replace(ACTION_PREFIX, "").replace(DETAIL_SUFFIX, "");

const trimSlash = (path) => (path.length > 1 ? path.replace(/\/+$/, "") : path).toLowerCase();

/**
 * Finds the leaf that best represents `pathname`, together with its parent group.
 * Exact matches win; otherwise detail/create/edit pages resolve to their list entry.
 */
export const findActive = (menu, pathname) => {
  const entries = [];
  menu.forEach((section) =>
    section.items.forEach((item) => {
      if (item.children) item.children.forEach((child) => entries.push({ leaf: child, group: item }));
      else entries.push({ leaf: item, group: null });
    })
  );

  const path = trimSlash(pathname);
  const exact = entries.find(({ leaf }) => trimSlash(leaf.to) === path);
  if (exact) return exact;

  const segment = firstSegment(path);
  const normalized = normalizeSegment(segment);
  const keysOf = (leaf) => [firstSegment(leaf.to), ...(leaf.also || [])];

  return (
    entries.find(({ leaf }) => keysOf(leaf).includes(segment)) ||
    entries.find(({ leaf }) => keysOf(leaf).includes(normalized)) ||
    null
  );
};

/** Filters the menu by a free-text query against item and group labels. */
export const searchMenu = (menu, query) => {
  const q = query.trim().toLowerCase();
  if (!q) return menu;
  const hit = (label) => label.toLowerCase().includes(q);

  return menu
    .map((section) => ({
      ...section,
      items: section.items
        .map((item) => {
          if (!item.children) return hit(item.label) ? item : null;
          if (hit(item.label)) return item;
          const children = item.children.filter((child) => hit(child.label));
          return children.length ? { ...item, children } : null;
        })
        .filter(Boolean),
    }))
    .filter((section) => section.items.length > 0);
};

export const initials = (name = "") =>
  name
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0].toUpperCase())
    .join("") || "U";
